import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronDown, ChevronUp, Plus, Trash } from 'lucide-react';
import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AddTableRow } from './AddTableRow';

export function AddQuestionRow({
  sectionId,
  subsectionId,
  groupId,
  onAddQuestionToGroup,
}: {
  sectionId: string;
  subsectionId: string;
  groupId: string;
  onAddQuestionToGroup: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    newQuestion: PolicyFormQuestion,
  ) => void;
}) {
  const QUESTION_TYPES: PolicyFormQuestion['type'][] = [
    'text',
    'number',
    'select',
    'multiselect',
    'radio',
    'date',
    'textarea',
    'table',
    'textselect',
  ];

  const [questionRef, setQuestionRef] = useState('');
  const [required, setRequired] = useState(false);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<PolicyFormQuestion['type']>('text');
  const [options, setOptions] = useState<{ id: string; value: string }[]>([]);
  const [newOpt, setNewOpt] = useState({ id: '', value: '' });
  const [showOptions, setShowOptions] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  const [columns, setColumns] = useState<PolicyFormTableColumn[]>([]);
  const [minRows, setMinRows] = useState<number | ''>('');
  const [maxRows, setMaxRows] = useState<number | ''>('');

  const handleAddOption = () => {
    if (!newOpt.id.trim() || !newOpt.value.trim()) return;
    setOptions([...options, newOpt]);
    setNewOpt({ id: '', value: '' });
  };

  const handleRemoveOption = (idx: number) => {
    setOptions(options.filter((_, i) => i !== idx));
  };

  const handleAddQuestion = () => {
    if (!questionRef.trim() || !title.trim()) return;

    const newQuestionId = crypto.randomUUID();

    const newQuestion: PolicyFormQuestion = {
      id: newQuestionId,
      questionRef,
      title,
      type,
      required,
      ...(type === 'select' || type === 'multiselect' || type === 'radio' ? { options } : {}),
      ...(type === 'table'
        ? {
            columns: columns,
            minRows: typeof minRows === 'number' ? minRows : undefined,
            maxRows: typeof maxRows === 'number' ? maxRows : undefined,
          }
        : {}),
    };

    onAddQuestionToGroup(sectionId, subsectionId, groupId, newQuestion);

    setQuestionRef('');
    setTitle('');
    setType('text');
    setOptions([]);
    setMinRows('');
    setMaxRows('');
    setColumns([]);
    setRequired(false);
    setShowOptions(false);
    setNewOpt({ id: '', value: '' });
  };

  if (!showAddRow) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="mt-2"
        onClick={() => setShowAddRow(true)}
      >
        <Plus
          size={16}
          className="mr-1"
        />{' '}
        Add Question
      </Button>
    );
  }

  return (
    <Card className="space-y-5 rounded-lg border bg-white p-5 shadow-sm">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1">
          <input
            value={questionRef}
            onChange={e => setQuestionRef(e.target.value)}
            placeholder="Question Ref"
            className="w-full border-b border-transparent bg-transparent
                   px-1 py-1
                   text-sm outline-none hover:bg-gray-50 focus:border-gray-400 focus:bg-gray-100"
          />
        </div>
        <div className="col-span-2">
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Question"
            className="w-full border-b border-transparent bg-transparent
                   px-1 py-1
                   text-sm outline-none hover:bg-gray-50 focus:border-gray-400 focus:bg-gray-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 items-center gap-4">
        <div className="col-span-2 flex items-center gap-3">
          <Select
            value={type}
            onValueChange={v => setType(v as any)}
          >
            <SelectTrigger className="w-56 hover:bg-gray-50 focus:bg-gray-100">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {QUESTION_TYPES.map(t => (
                <SelectItem
                  key={t}
                  value={t}
                >
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(type === 'select' || type === 'multiselect' || type === 'radio') && (
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
              onClick={() => setShowOptions(prev => !prev)}
            >
              {showOptions ? (
                <>
                  <ChevronUp size={14} /> Hide Options
                </>
              ) : (
                <>
                  <ChevronDown size={14} /> View Options
                </>
              )}
            </button>
          )}

          {/* Required toggle */}
          <button
            type="button"
            onClick={() => setRequired(!required)}
            className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors
        ${required ? 'bg-red-500 text-white' : 'border border-red-500 bg-white text-red-700'}`}
            title={required ? 'Required' : 'Not Required'}
          >
            R
          </button>
        </div>

        {/* Right: Save + Cancel */}
        <div className="col-span-1 flex justify-end gap-2">
          <Button
            size="sm"
            onClick={handleAddQuestion}
            className="flex items-center gap-1"
          >
            <Plus size={12} /> Save
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setShowAddRow(false)}
          >
            Cancel
          </Button>
        </div>
      </div>

      {/* Row 3: Options */}
      {showOptions && (type === 'select' || type === 'multiselect' || type === 'radio') && (
        <div className="space-y-3 border-t pt-3">
          {options.map((opt, idx) => (
            <div
              key={opt.id}
              className="flex items-center gap-2 pt-2"
            >
              <input
                value={opt.id}
                onChange={e => {
                  const updated = [...options];
                  updated[idx] = { ...opt, id: e.target.value };
                  setOptions(updated);
                }}
                placeholder="ID"
                className="w-40 rounded border px-2 py-1 font-mono text-xs
                         hover:bg-gray-50 focus:bg-gray-100"
              />
              <input
                value={opt.value}
                onChange={e => {
                  const updated = [...options];
                  updated[idx] = { ...opt, value: e.target.value };
                  setOptions(updated);
                }}
                placeholder="Value"
                className="flex-1 rounded border px-2 py-1 text-sm
                         hover:bg-gray-50 focus:bg-gray-100"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveOption(idx)}
                className="text-red-700 hover:text-red-700"
              >
                <Trash size={14} />
              </Button>
            </div>
          ))}

          {/* Add new option row */}
          <div className="flex items-center gap-2 pt-2">
            <input
              type="text"
              value={newOpt.id}
              onChange={e => setNewOpt({ ...newOpt, id: e.target.value })}
              className="w-40 rounded border px-2 py-1 font-mono text-sm
                       hover:bg-gray-50 focus:bg-gray-100"
              placeholder="New Option ID"
            />
            <input
              type="text"
              value={newOpt.value}
              onChange={e => setNewOpt({ ...newOpt, value: e.target.value })}
              className="flex-1 rounded border px-2 py-1 text-sm
                       hover:bg-gray-50 focus:bg-gray-100"
              placeholder="New Option Value"
            />
            <button
              type="button"
              className="flex items-center text-xs text-green-600 hover:text-green-700"
              onClick={handleAddOption}
            >
              <Plus
                size={14}
                className="mr-1"
              />{' '}
              Add
            </button>
          </div>
        </div>
      )}

      {/* Table Columns */}
      {type === 'table' && (
        <AddTableRow
          columns={columns}
          setColumns={setColumns}
          minRows={minRows}
          setMinRows={setMinRows}
          maxRows={maxRows}
          setMaxRows={setMaxRows}
        />
      )}
    </Card>
  );
}
